import { FEATURE_FLAG as CONSENT_FEATURE_FLAG } from '@buzzfeed/consent';
import { CLUSTER } from '../constants';
import EditorialStatus from './editorial-status';
import { isGamesPost } from '../utils/games';
import { isNews } from '../utils/isNews';
import { isOrHasQuiz } from '../utils/isOrHasQuiz';
import { getUserGeoCountry } from '@buzzfeed/bf-utils/lib/localization';
import { isSponsored } from '../utils/isSponsored';
import {
  CARD_STYLE_EXPERIMENT,
  CARD_STYLE_SUPPRESSED_VARIANTS,
  isCardStyleExperimentEligible,
} from '../utils/cardStyleQuizExperiment';
import { isIDNML } from '../utils/idnml';
import { isCardStyleQuizAdInjectionExperimentEligible } from '../utils/cardStyleQuizAdInjectionExperiment';
import { isIOSAgent } from '@buzzfeed/bf-utils/lib/device';
import { isWordChain, isHuedoku, isSoupOfTheDay, isGeotemp } from '../utils/gameInterstitial';
import { isBFAppWebview } from '@buzzfeed/bf-utils/lib/webview';

const featureFlags = [
  {
    name: 'ads_bullwhip',
    variations: ['control', 'on'],
    isEligible: () => true,
  },
  {
    name: 'TimeSpentRO_4',
    variations: ['on', 'control'],
    isEligible: () => true,
  },
  {
    name: 'PAR-where-to-watch-release',
    variations: ['control', 'on'],
    isEligible: () => true,
  },
  {
    name: 'ads_destroy_ads_on_scroll_mobile',
    variations: ['control', 'on'],
    isEligible: (buzz) =>
      !['gb', 'ie', 'uk', 'au', 'nz'].includes(getUserGeoCountry().toLowerCase()) &&
      buzz.classification.edition !== 'Japan' &&
      !buzz.isAd &&
      !isOrHasQuiz(buzz) &&
      !window.matchMedia('(min-width: 40rem)').matches, // xs,
  },
  {
    name: 'ads_destroy_ads_on_scroll_quiz',
    variations: ['control', 'on'],
    isEligible: (buzz) =>
      !['gb', 'ie', 'uk', 'au', 'nz'].includes(getUserGeoCountry().toLowerCase()) &&
      buzz.classification.edition !== 'Japan' &&
      !buzz.isAd &&
      isOrHasQuiz(buzz)
  },
  {
    name: 'RT-1559-AdShield-script-on-BFDC',
    variations: ['control', 'on'],
    isEligible: (buzz) => !isNews(buzz),
  },
  {
    name: 'RT-1559-AdShield-script-on-BFN',
    variations: ['control', 'on'],
    isEligible: (buzz) => isNews(buzz),
  },
  {
    name: 'bf_crash_monitor_enabled',
    variations: ['control', 'on'],
    isEligible: () => true,
  },
  {
    name: 'bf_taboola_push_notifications_enabled',
    variations: ['control', 'on'],
    isEligible: (buzz) => isIDNML({ isBFN: isNews(buzz) }),
  },
  {
    name: 'ads_amazon_nca',
    variations: ['control', 'on'],
    isEligible: () => true,
  },
  {
    name: 'taboola_apac',
    variations: ['control', 'on'],
    isEligible: (buzz) => buzz.classification.edition !== 'Japan',
  },
  {
    name: 'RT-1522-sidebar-15s-refresh',
    variations: ['control', 'on'],
    isEligible: () =>
      window.matchMedia('(min-width: 40rem)').matches &&
      !['gb', 'ie', 'uk'].includes(getUserGeoCountry().toLowerCase())
  },
  {
    name: 'RT-1510-ButtonPostTap-Non-Amazon',
    variations: ['control', 'on'],
    isEligible: (buzz) => buzz.isShopping && !buzz.isAd && buzz.isUS && buzz.destination_name === 'buzzfeed',
  },
  {
    name: 'RT-1378-us-quiz-top-sticky-desktop',
    variations: ['control', 'on'],
    isEligible: (buzz) =>
      isOrHasQuiz(buzz) &&
      !buzz.isAd &&
      buzz.isUS &&
      getUserGeoCountry().toLowerCase() !== 'gb' &&
      getUserGeoCountry().toLowerCase() !== 'ie' &&
      getUserGeoCountry().toLowerCase() !== 'au' &&
      getUserGeoCountry().toLowerCase() !== 'nz' &&
      window.matchMedia('(min-width: 40rem)').matches,
  },
  {
    name: 'ads-quiz-top-player-mobile',
    variations: ['control', 'on'],
    isEligible: (buzz) =>
      isOrHasQuiz(buzz) &&
      !buzz.isAd &&
      buzz.isUS &&
      getUserGeoCountry().toLowerCase() !== 'gb' &&
      getUserGeoCountry().toLowerCase() !== 'ie' &&
      getUserGeoCountry().toLowerCase() !== 'au' &&
      getUserGeoCountry().toLowerCase() !== 'nz' &&
      !window.matchMedia('(min-width: 40rem)').matches, // xs,
  },
  {
    name: 'taboola_au',
    variations: ['control', 'on'],
    isEligible: (buzz) => buzz.classification.edition !== 'Japan',
  },
  {
    name: 'ads_interstitial',
    variations: ['control', 'on'],
    isEligible: (buzz) =>
      getUserGeoCountry().toLowerCase() === 'us'
      // page level break is based on bf_header_ui break
      && window.matchMedia('(max-width: 500px)').matches
      && !buzz.isShopping && !isOrHasQuiz(buzz)
      && !(buzz?.laser_tags?.bf_content_description?.workflow || []).some(
        tag => tag.tag_name === 'generator_generator'
      )
  },
  {
    name: 'Ads-Connatix-Backfill-Player',
    variations: ['control', 'on'],
    isEligible: () => true,
  },
  {
    name: 'RT-2332-sticky-connatix-desktop-bf',
    variations: ['control', 'on'],
    isEligible: (buzz) =>
      !['gb', 'ie', 'uk', 'au', 'jp'].includes(getUserGeoCountry().toLowerCase()) &&
      !['UK', 'Japan', 'Australia', 'AU'].includes(buzz?.classification?.edition) &&
      window.matchMedia('(min-width: 1026px)').matches,
  },
  {
    name: 'RT-1559-AdShield-script-on-BFDC',
    variations: ['control', 'on'],
    isEligible: (buzz) => !['gb', 'ie', 'uk', 'nz', 'au', 'jp'].includes(getUserGeoCountry().toLowerCase()) && !isNews(buzz)
  },
  {
    name: 'RT-2426-k-words-bfnews',
    variations: ['control', 'on'],
    isEligible: (buzz) =>
      !buzz.disableAds &&
      getUserGeoCountry().toLowerCase() === 'us' &&
      !buzz.isAd &&
      isNews(buzz) &&
      !isOrHasQuiz(buzz) &&
      !isGamesPost(buzz),
  },
];

// --- Tag Yourself eligibility helpers (BF-17627-rollout-bfp-quiz-tag-yourself) ---
// Why this is duplicated here (and not only in bfp_quiz):
// bf_bpage_ui owns experiment participation tracking — its ABeagleProvider fires the
// consolidated trackClientExperimentActive event for ALL page-level experiments.
// bfp_quiz's ABeagleProvider does NOT fire tracking. Without this entry, the experiment
// would have no analytics record. The isEligible checks must match bfp_quiz's so that
// users on ineligible quizzes are not tracked as participants with no exposure.
// TODO(BF-17757-cleanup): After experiment graduation, move this title
// eligibility logic into the long-term shared Tag Yourself helper location.
// TODO(BF-17757-cleanup): bf_bpage_ui should no longer carry bfp_quiz
// eligibility logic in ab-tests.js or anywhere else once that shared path exists.
const TAG_YOURSELF_TITLE_THRESHOLDS_BY_LAYOUT = {
  twoColumn: {
    characterCount: 36,
    wordCount: 7,
  },
  threeColumn: {
    characterCount: 32,
    wordCount: 6,
  },
  fourColumn: {
    characterCount: 28,
    wordCount: 5,
  },
};

const TAG_YOURSELF_DEBUG_PREFIX = 'TAG YOURSELF DEBUG [bf_bpage_ui]';


const getBfpQuizSubbuzzes = (buzz) =>
  (buzz?.sub_buzzes || []).filter((subbuzz) => subbuzz.form === 'bfp');

const getBfpQuizResults = (buzz) =>
  getBfpQuizSubbuzzes(buzz).flatMap(
    (subbuzz) => subbuzz?.bfp_data?.data?.results || []
  );

const getBfpQuizQuestions = (buzz) =>
  getBfpQuizSubbuzzes(buzz).flatMap(
    (subbuzz) => subbuzz?.bfp_data?.data?.questions || []
  );

const getTagYourselfTitleLayout = (resultsCount) => {
  if (resultsCount <= 4) return 'twoColumn';
  if (resultsCount <= 9) return 'threeColumn';
  return 'fourColumn';
};

const isQuizEligibilityDebugEnabled = () => {
  if (
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('debugQuizEligibility')
  ) {
    return true;
  }

  return CLUSTER === 'stage';
};

const getTagYourselfTitleMetrics = (title = '') => {
  const words = title.trim().split(/\s+/).filter(Boolean);

  return {
    characterCount: title.length,
    wordCount: words.length,
  };
};

const isTagYourselfTitleEligible = (title, thresholds, layout) => {
  const titleMetrics = getTagYourselfTitleMetrics(title);

  const isEligible =
    titleMetrics.characterCount <= thresholds.characterCount &&
    titleMetrics.wordCount <= thresholds.wordCount;

  if (!isEligible && isQuizEligibilityDebugEnabled()) {
    console.log(`${TAG_YOURSELF_DEBUG_PREFIX}: result failed title eligibility`, {
      title,
      metrics: titleMetrics,
      thresholds,
      layout,
    });
  }

  return isEligible;
};

const areTagYourselfTitlesEligible = (results) => {
  if (!results?.length) return false;

  const layout = getTagYourselfTitleLayout(results.length);
  const thresholds = TAG_YOURSELF_TITLE_THRESHOLDS_BY_LAYOUT[layout];

  return results.every((result) =>
    isTagYourselfTitleEligible(result?.title || '', thresholds, layout)
  );
};

const isTagYourselfDataEligible = (results) => {
  if (!results || results.length < 2) return false;

  return results.every((result) => {
    const hasUrl = Boolean(result?.media?.url);
    const hasTitle = Boolean(result?.title);
    const isEligible = hasUrl && hasTitle;

    if (!isEligible && isQuizEligibilityDebugEnabled()) {
      console.log(`${TAG_YOURSELF_DEBUG_PREFIX}: result failed data eligibility`, {
        title: result?.title,
        url: result?.media?.url,
        media: result?.media,
        hasUrl,
        hasTitle,
      });
    }

    return isEligible;
  });
};

const isTagYourselfAllowedEditorialStatus = (editorialStatus) => {
  return [
    EditorialStatus.EDITORIAL,
    EditorialStatus.MODERATED_COMMUNITY,
  ].includes(editorialStatus);
};

const isScalePersonalityQuiz = (buzz) => {
  if (
    (buzz?.bfpFormats || []).some(
      (format) => format === 'scale_quiz' || format === 'slider_quiz'
    )
  ) {
    return true;
  }

  return getBfpQuizQuestions(buzz).some((question) => question?.type === 'scale');
};
// --- end Tag Yourself eligibility helpers ---

const abTests = [
  {
    name: 'ri_plushie_filter_login',
    variations: ['control', 'variant'],
    isEligible: (buzz) =>
      buzz.isUS && getUserGeoCountry().toLowerCase() === 'us',
  },
  {
    name: 'RT-1150-ButtonPostTap-abtest',
    variations: ['control', 'no_button_wrap'],
    isEligible: (buzz) => buzz.isUS && buzz.destination_name === 'buzzfeed',
  },
  {
    name: 'ads_retail_media_network_exposure',
    variations: ['control', 'rmn_off'],
    isEligible: (buzz) => buzz.isShopping && !buzz.isAd && buzz.isUS,
  },
  {
    name: 'ads_shopping_infinite_scroll_v2',
    variations: ['control', 'is_v2'],
    isEligible: (buzz) => buzz.isUS && getUserGeoCountry().toLowerCase() === 'us' && buzz.destination_name === 'buzzfeed' && buzz.isShopping,
  },
  {
    name: 'ads_permutive_non_shopping',
    variations: ['control', 'off'],
    isEligible: (buzz) => {
      const isUS = getUserGeoCountry().toLowerCase() === 'us';
      return !buzz.isShopping && (!isUS || !isOrHasQuiz(buzz));
    },
  },
  {
    name: 'BF-15206-quiz-recirc-improvement',
    variations: ['control', 'variant_1', 'variant_2'],
    isEligible: (buzz) => (
      buzz.is_quiz && buzz.classification.edition === 'US'
    )
  },
  {
    name: 'BF-14967-Callout-Unit-Quiz-Action-Triggers',
    variations: ['control', 'action'],
    isEligible: (buzz) => (
      buzz.isUS &&
      !window.location.search.includes('origin=sdxa') &&
      !isNews(buzz) &&
      !isSponsored(buzz) &&
      !(buzz?.laser_tags?.bf_content_description?.workflow || []).some(
        tag => tag.tag_name === 'generator_generator'
      )
    )
  },
  {
    name: 'BF-14963-Callout-Unit-Quiz-Trigger-Delay',
    variations: ['control', 'seconds_4', 'seconds_7', 'seconds_14'],
    isEligible: (buzz) => (
      buzz.isUS &&
      !window.location.search.includes('origin=sdxa') &&
      !isNews(buzz) &&
      !isSponsored(buzz) &&
      !(buzz?.laser_tags?.bf_content_description?.workflow || []).some(
        tag => tag.tag_name === 'generator_generator'
      )
    )
  },
  {
    name: 'BF-15681-Callout-Unit-Shopping-Gift-Guides',
    variations: ['control', 'v1', 'v2', 'v3'],
    isEligible: (buzz) => (
      buzz.isUS &&
      !window.location.search.includes('origin=sdxa') &&
      !isNews(buzz) &&
      !isSponsored(buzz) &&
      !(buzz?.laser_tags?.bf_content_description?.workflow || []).some(
        tag => tag.tag_name === 'generator_generator'
      )
    )
  },
  {
    name: 'BF-16768-Tag-Yourself-No-Callout-Unit',
    variations: ['control', 'v1', 'v2'],
    hideCalloutForVariants: ['control', 'v1', 'v2'],
    isEligible: (buzz) => (
      (buzz?.laser_tags?.bf_content_description?.workflow || []).some(
        tag => tag.tag_name === 'tag_yourself_quiz_experiment'
      )
    ),
  },
  {
    name: 'BF-17997-quiz-party-complete-copy',
    variations: ['control', 'v1', 'v2', 'v3'],
    isEligible: (buzz) => (
      buzz.isQuiz &&
      isIOSAgent()
    ),
  },
  // TODO(BF-17627-cleanup): Remove this entry when experiment graduates.
  {
    name: 'BF-17627-rollout-bfp-quiz-tag-yourself',
    variations: ['control', 'v1'],
    hideCalloutForVariants: ['control', 'v1'],
    isEligible: (buzz) => {
      const results = getBfpQuizResults(buzz);
      const isScaleQuiz = isScalePersonalityQuiz(buzz);
      const hasAllowedEditorialStatus = isTagYourselfAllowedEditorialStatus(
        buzz.editorial_status
      );
      const hasExcludedLaserTag = (buzz?.laser_tags?.bf_content_description?.frames_and_formats || []).some(
        (tag) => ['can_we_guess_quiz'].includes(tag.tag_name)
      );
      const hasExcludedTag = (buzz?.tags || []).some((tag) =>
        [
          // Age-related quizzes
          'can-we-guess-your-age',
          'can we guess your age',
          'guess your age',
          'age',
          'agequiz',
          // Scale personality quizzes
          'scalequiz',
          'scale_quiz',
          'slider_quiz',
        ].includes(tag)
      );
      const isPersonality = buzz.isPersonality;
      const isUSEdition = buzz.isUS;
      const hasEligibleData = isTagYourselfDataEligible(results);
      const hasEligibleTitles = areTagYourselfTitlesEligible(results);

      if (isQuizEligibilityDebugEnabled()) {
        console.log(`${TAG_YOURSELF_DEBUG_PREFIX}: eligibility summary`, {
          isPersonality,
          isScaleQuiz,
          isUSEdition,
          editorialStatus: buzz.editorial_status,
          hasAllowedEditorialStatus,
          hasExcludedLaserTag,
          hasExcludedTag,
          isTagYourselfDataEligible: hasEligibleData,
          areTagYourselfTitlesEligible: hasEligibleTitles,
          results,
        });
      }

      return (
        isPersonality &&
        isUSEdition &&
        // IMPORTANT: These eligibility checks must stay in sync with the isEligible callback
        // for BF-17627-rollout-bfp-quiz-tag-yourself in bfp_quiz/src/components/Render/constants/ab-tests.js.
        // Both services must bucket users identically. If you change any condition here,
        // make the same change there and preserve identical behavior and debug logging.
        hasAllowedEditorialStatus &&
        !isScaleQuiz &&
        !hasExcludedLaserTag &&
        !hasExcludedTag &&
        hasEligibleData &&
        hasEligibleTitles
      );
    }
  },
  {
    name: CARD_STYLE_EXPERIMENT,
    variations: ['control', ...CARD_STYLE_SUPPRESSED_VARIANTS],
    hideCalloutForVariants: CARD_STYLE_SUPPRESSED_VARIANTS,
    isEligible: isCardStyleExperimentEligible,
  },
  {
    name: 'RT-2202-ad-injection-card-style-quiz',
    variations: ['control', 'v1', 'v2', 'v3'],
    hideCalloutForVariants: ['control', 'v1', 'v2', 'v3'],
    isEligible: (buzz) =>
      !window.matchMedia('(min-width: 40rem)').matches && // xs and below
      !buzz.isPreview &&
      !buzz.isAd &&
      isCardStyleQuizAdInjectionExperimentEligible(buzz),
  },
  {
    name: 'RT-2238-remove-primeday-ynft',
    variations: ['control', 'no_primeday', 'time_spent'],
    isEligible: (buzz) => buzz.isUS && !buzz.isAd,
  },
  {
    name: 'word-chain-interstitial',
    variations: ['control', 'level_3_ad', 'results_ad'],
    isEligible: (buzz) =>
      !window.matchMedia('(min-width: 40rem)').matches && // xs and below,
      !isBFAppWebview() &&
      isWordChain(buzz),
  },
  {
    name: 'huedoku-interstitial',
    variations: ['control', 'level_1_ad'],
    isEligible: (buzz) =>
      !window.matchMedia('(min-width: 40rem)').matches &&
      !isBFAppWebview() &&
      isHuedoku(buzz),
  },
  {
    name: 'soup-of-the-day-interstitial',
    variations: ['control', 'give_up'],
    isEligible: (buzz) =>
      !window.matchMedia('(min-width: 40rem)').matches && // xs and below,
      !isBFAppWebview() &&
      isSoupOfTheDay(buzz),
  },
  {
    name: 'geotemp-interstitial',
    variations: ['control', 'level_2_ad', 'level_3_ad', 'see_your_score'],
    isEligible: (buzz) =>
      !window.matchMedia('(min-width: 40rem)').matches && // xs and below,
      !isBFAppWebview() &&
      isGeotemp(buzz),
  },
  {
    name: 'RT-2335-k-words',
    variations: ['control', 'on'],
    isEligible: (buzz) =>
      !buzz.disableAds &&
      getUserGeoCountry().toLowerCase() === 'us' &&
      !buzz.isAd &&
      !isNews(buzz) &&
      !buzz.isShopping &&
      !isOrHasQuiz(buzz) &&
      !isGamesPost(buzz),
  },
  {
    name: 'RT-2424-sticky-connatix-desktop-games',
    variations: ['control', 'on'],
    isEligible: (buzz) => {
      return (
      (
        isOrHasQuiz(buzz) ||
        isGamesPost(buzz)
      ) &&
      !buzz.isAd &&
      window.matchMedia('(min-width: 1026px)').matches);
    }

  }
];

const combinedFlags = [
  CONSENT_FEATURE_FLAG,
  ...featureFlags,
  ...abTests,
];
export default combinedFlags;

export {
  TAG_YOURSELF_TITLE_THRESHOLDS_BY_LAYOUT,
  getTagYourselfTitleMetrics,
  isQuizEligibilityDebugEnabled,
  isTagYourselfDataEligible,
};
